import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiPaperclip, FiFile, FiDownload, FiExternalLink, FiLink } from 'react-icons/fi';
import { emailsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import './EmailLogAttachments.css';

export function parseAttachmentNames(metadata) {
  if (!metadata) return [];
  try {
    const obj = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
    const raw = obj?.attachments || [];
    return raw.map((a) => (typeof a === 'string' ? a : a?.name || '')).filter(Boolean);
  } catch (_) {
    return [];
  }
}

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return '';
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

async function interpretDownloadResponse(res, att) {
  const ct = (res.headers['content-type'] || '').split(';')[0].trim().toLowerCase();
  if (ct === 'application/json') {
    const text = await res.data.text();
    let j;
    try {
      j = JSON.parse(text);
    } catch (_) {
      throw new Error('Invalid server response');
    }
    if (j.link && j.sourceUrl) {
      return { kind: 'link', sourceUrl: j.sourceUrl, name: j.name };
    }
    throw new Error(j.message || 'Download failed');
  }
  return {
    kind: 'blob',
    blob: new Blob([res.data], {
      type: ct || att.contentType || 'application/octet-stream'
    })
  };
}

/**
 * Lists Graph-backed attachments for an email log; falls back to metadata names when Graph is unavailable.
 * @param {number} emailLogId
 * @param {string|null|undefined} graphMessageId
 * @param {object|string|null|undefined} metadata — outbound send metadata with attachment names
 * @param {boolean} [compact] — tighter layout for thread bubbles
 */
export default function EmailLogAttachments({ emailLogId, graphMessageId, metadata, compact }) {
  const { t } = useTranslation();
  const fallbackNames = useMemo(() => parseAttachmentNames(metadata), [metadata]);
  const shouldShow = fallbackNames.length > 0 || !!graphMessageId;
  const [graphItems, setGraphItems] = useState(null);
  const [graphError, setGraphError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!emailLogId || !shouldShow) {
      setGraphItems(null);
      setGraphError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setGraphError(null);
    emailsAPI
      .getEmailLogAttachments(emailLogId)
      .then((res) => {
        if (!cancelled) setGraphItems(res.data?.data ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          setGraphItems(null);
          setGraphError(err.response?.data?.message || err.message || 'Could not load files');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [emailLogId, shouldShow, graphMessageId, fallbackNames.join('|')]);

  if (!shouldShow) return null;

  const openOrFetchLink = async (att) => {
    if (att.attachmentType === 'reference' && att.sourceUrl) {
      window.open(att.sourceUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const res = await emailsAPI.downloadEmailLogAttachment(emailLogId, att.id);
      const parsed = await interpretDownloadResponse(res, att);
      if (parsed.kind === 'link') {
        window.open(parsed.sourceUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || t('emailAttachments.couldNotOpenLink'));
    }
  };

  const getBlobForFile = async (att) => {
    const res = await emailsAPI.downloadEmailLogAttachment(emailLogId, att.id);
    const parsed = await interpretDownloadResponse(res, att);
    if (parsed.kind === 'link') {
      window.open(parsed.sourceUrl, '_blank', 'noopener,noreferrer');
      return null;
    }
    return parsed.blob;
  };

  const download = async (att) => {
    if (att.attachmentType === 'reference') {
      await openOrFetchLink(att);
      return;
    }
    if (att.attachmentType === 'item') {
      toast.info(t('emailAttachments.embeddedOutlookToast'));
      return;
    }
    try {
      const blob = await getBlobForFile(att);
      if (!blob) return;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.name || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || t('emailAttachments.downloadFailed'));
    }
  };

  const view = async (att) => {
    if (att.attachmentType === 'reference') {
      await openOrFetchLink(att);
      return;
    }
    if (att.attachmentType === 'item') {
      toast.info(t('emailAttachments.embeddedOutlookToast'));
      return;
    }
    try {
      const blob = await getBlobForFile(att);
      if (!blob) return;
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60 * 1000);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || t('emailAttachments.unableToOpen'));
    }
  };

  const rootClass = `ela-root${compact ? ' ela-compact' : ''}`;

  return (
    <div className={`ela-att-section ${rootClass}`}>
      <div className="ela-att-label">
        <FiPaperclip size={compact ? 10 : 11} />
        {t('emailAttachments.title')}
      </div>

      {loading && (
        <div style={{ fontSize: compact ? 11 : 12, color: 'var(--ela-text3)' }}>{t('emailAttachments.loadingOutlook')}</div>
      )}

      {!loading && graphItems && graphItems.length > 0 && (
        <div className="ela-att-list">
          {graphItems.map((att) => (
            <div key={att.id} className="ela-att-item">
              <FiFile size={compact ? 13 : 14} style={{ color: 'var(--ela-accent)', flexShrink: 0 }} />
              <span className="ela-att-name">{att.name}</span>
              {att.size > 0 && <span className="ela-att-size">{formatFileSize(att.size)}</span>}

              {att.attachmentType === 'reference' && (
                <button type="button" className="ela-btn" onClick={() => openOrFetchLink(att)}>
                  <FiLink size={12} /> {t('emailAttachments.openLink')}
                </button>
              )}

              {att.attachmentType === 'item' && (
                <span className="ela-att-muted">{t('emailAttachments.embeddedItem')}</span>
              )}

              {(att.attachmentType === 'file' || !att.attachmentType) && (
                <>
                  <button type="button" className="ela-btn" onClick={() => view(att)}>
                    <FiExternalLink size={12} /> {t('emailAttachments.viewBtn')}
                  </button>
                  <button type="button" className="ela-btn" onClick={() => download(att)}>
                    <FiDownload size={12} /> {t('emailAttachments.downloadBtn')}
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && (!graphItems || graphItems.length === 0) && fallbackNames.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {fallbackNames.map((name, i) => (
            <span key={`${name}-${i}`} className="ela-att-fallback-chip">
              <FiFile size={12} /> {name}
            </span>
          ))}
          {graphError && (
            <span style={{ fontSize: 11, color: 'var(--ela-text3)' }} title={graphError}>
              (
              {graphMessageId
                ? t('emailAttachments.couldNotOpenOutlook')
                : t('emailAttachments.notLinkedOutlook')}
              )
            </span>
          )}
        </div>
      )}

      {!loading && graphItems && graphItems.length === 0 && fallbackNames.length === 0 && graphMessageId && (
        <div style={{ fontSize: compact ? 11 : 12, color: 'var(--ela-text3)' }}>
          {t('emailAttachments.noAttachments')}
        </div>
      )}
      {!loading && graphError && graphItems === null && fallbackNames.length === 0 && (
        <div style={{ fontSize: compact ? 11 : 12, color: 'var(--ela-text3)' }}>{graphError}</div>
      )}
    </div>
  );
}
